import { useState, useEffect } from 'react';
import api from '../lib/api';
import BpmnEditor from './BpmnEditor';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Save, 
  Loader2,
  X,
  Plus,
  Map
} from 'lucide-react';
import { toast } from 'sonner';

const ProcessMapModal = ({ 
  open, 
  onClose, 
  onSave,
  entityType, // 'idea' or 'project'
  entityId,
  entityName
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [l2Processes, setL2Processes] = useState([]);
  const [templateXml, setTemplateXml] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    department: '',
    process_area: '',
    version: '1.0',
    tags: [],
    xml_content: ''
  });
  
  const [tagInput, setTagInput] = useState('');
  const [xmlContent, setXmlContent] = useState('');

  useEffect(() => {
    if (open) {
      fetchSettings();
      fetchTemplate();
      // Reset form when opening
      setFormData({
        name: '',
        description: '',
        department: '',
        process_area: '',
        version: '1.0',
        tags: [],
        xml_content: ''
      });
    }
  }, [open]);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/processes?level=2');
      setL2Processes(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      const response = await api.get('/process-maps/template');
      setTemplateXml(response.data.xml);
      setXmlContent(response.data.xml);
      setFormData(prev => ({ ...prev, xml_content: response.data.xml }));
    } catch (error) {
      console.error('Failed to fetch template:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleXmlChange = (newXml) => {
    setXmlContent(newXml);
    setFormData(prev => ({ ...prev, xml_content: newXml }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a name for the process map');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        xml_content: xmlContent || templateXml
      };

      // Create the process map
      const response = await api.post('/process-maps', payload);
      const newMapId = response.data.id;
      
      // Link it to the entity
      if (entityId && entityType) {
        await api.post(`/process-maps/${newMapId}/link?entity_type=${entityType}&entity_id=${entityId}`);
      }
      
      toast.success('Process map created and linked');
      
      if (onSave) {
        onSave({
          id: newMapId,
          name: formData.name,
          description: formData.description,
          department: formData.department,
          status: 'Draft',
          version: formData.version
        });
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to save process map:', error);
      toast.error('Failed to create process map');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] w-[1400px] h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Map className="w-5 h-5 text-[#235894]" />
              <DialogTitle className="text-xl">Create Process Map</DialogTitle>
              {entityName && (
                <Badge variant="outline" className="text-xs">
                  Linking to: {entityName}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave}
                disabled={saving || !formData.name.trim()}
                className="bg-[#235894] hover:bg-[#1E4B80]"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Create & Link
              </Button>
            </div>
          </div>
        </DialogHeader>
        
        <div className="flex-1 flex overflow-hidden">
          {/* Properties Panel */}
          <div className="w-72 border-r p-4 overflow-y-auto bg-slate-50">
            <h3 className="font-medium text-slate-900 mb-4">Properties</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Current Invoice Process"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description..."
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor="department">Department</Label>
                <Select
                  value={formData.department}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, department: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {l2Processes.map((p) => (
                      <SelectItem key={p.id} value={p.name}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="version">Version</Label>
                <Input
                  id="version"
                  value={formData.version}
                  onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                  placeholder="1.0"
                />
              </div>

              <div>
                <Label>Tags</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    placeholder="Add tag..."
                    className="flex-1"
                  />
                  <Button variant="outline" size="icon" onClick={handleAddTag}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {formData.tags.map((tag, i) => (
                      <Badge key={i} variant="secondary" className="gap-1 text-xs">
                        {tag}
                        <button onClick={() => handleRemoveTag(tag)}>
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="pt-4 border-t">
                <p className="text-xs text-slate-500">
                  💡 Tip: Use tags like "as-is", "to-be", "current", "future" to categorize your process maps.
                </p>
              </div>
            </div>
          </div>

          {/* BPMN Editor */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#235894]" />
              </div>
            ) : (
              <BpmnEditor
                xml={templateXml}
                onXmlChange={handleXmlChange}
                height={600}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProcessMapModal;
